// Типы для Bingx
export interface PendingBingxItem {
    id: number;
    user_id: number;
    amount: number;
    currency: string;
    created_at: string;
    api_seen?: boolean;
    api_amount?: number | null;
    api_status?: string;
    api_message?: string | null;
}

export interface BingXAddress {
    id: number;
    network: "trc20" | "bep20" | "ton" | string;
    address: string;
    status: number;
    source: string;
    busy: boolean;
    busy_user_id?: number | null;
}

// Типы для BingX кошельков (страница CryptoWalletsPage использует эти типы)
export interface BingXWallet {
    alny_bingx_accounts_id: number;
    alny_bingx_accounts_account: string;
    alny_bingx_accounts_sum_total: number;
    /** оплачені заявки по адресах акаунта, весь час */
    credited_total?: number;
    sum_total_reset_at?: number | null;
    /** що показали юзерам з моменту сбросу */
    shown_since_reset?: number | null;
    /** sum_total — залічено в Хаб з моменту сбросу */
    credited_since_reset?: number;
    alny_bingx_accounts_trc20: string;
    alny_bingx_accounts_bep20: string;
    alny_bingx_accounts_ton: string;
    alny_bingx_accounts_sum: number;
    alny_bingx_accounts_user_id: number;
    alny_bingx_accounts_start: number;
    alny_bingx_accounts_trans: number;
    alny_bingx_accounts_type: "trc20" | "bep20" | "ton" | "";
    alny_bingx_accounts_status: number;
    api_key?: string;
    secret_key?: string;
    has_api_keys?: boolean;
    addresses?: BingXAddress[];
    address_counts?: {
        bep20: number;
        trc20: number;
        ton: number;
        busy: number;
    };
    created_at?: string;
    updated_at?: string;
}

export interface BingXTopUpRow {
    id: number;
    user_id: number;
    user_name?: string | null;
    user_secret?: string | null;
    network: string;
    address: string;
    requested: number | null;
    received: number | null;
    diff: number | null;
    status: number;
    tx_hash: string | null;
    time: number;
    source?: "hub" | "millennium" | string;
    order_id?: string | null;
}

export interface CreateBingXWalletRequest {
    alny_bingx_accounts_account: string;
    alny_bingx_accounts_trc20?: string;
    alny_bingx_accounts_bep20?: string;
    alny_bingx_accounts_ton?: string;
    alny_bingx_accounts_sum_total?: number;
    alny_bingx_accounts_sum?: number;
    alny_bingx_accounts_user_id?: number;
    alny_bingx_accounts_start?: number;
    alny_bingx_accounts_trans?: number;
    alny_bingx_accounts_type?: "trc20" | "bep20" | "ton" | "";
    alny_bingx_accounts_status?: number;
    api_key?: string;
    secret_key?: string;
}

export const bingxEndpoints = {
    getPendingBingx: {
        query: () => "/admin/bingx/pending",
    },

    confirmBingx: {
        query: (id: number) => ({
            url: `/admin/bingx/${id}/confirm`,
            method: "POST",
        }),
    },

    cancelBingxRequest: {
        query: (id: number) => ({
            url: `/admin/bingx/${id}/cancel`,
            method: "POST",
        }),
    },

    // Crypto Wallets (BingX Accounts) CRUD
    getBingXWallets: {
        query: () => "/alny-bingx-accounts",
    },
    createBingXWallet: {
        query: (data: any) => ({
            url: "/alny-bingx-accounts",
            method: "POST",
            body: data,
        }),
    },
    updateBingXWallet: {
        query: (args: any) => {
            const { id, ...body } = args;
            return {
                url: `/alny-bingx-accounts/${id}`,
                method: "PUT",
                body: body,
            };
        },
    },
    deleteBingXWallet: {
        query: (id: number) => ({
            url: `/alny-bingx-accounts/${id}`,
            method: "DELETE",
        }),
    },
    syncBingXAddresses: {
        query: (id: number) => ({
            url: `/alny-bingx-accounts/${id}/sync-addresses`,
            method: "POST",
        }),
    },
    getBingXBalance: {
        query: (id: number) => `/alny-bingx-accounts/${id}/balance`,
    },
    getBingXWithdrawals: {
        query: (args: { id: number; days?: number }) =>
            `/alny-bingx-accounts/${args.id}/withdrawals?days=${args.days ?? 30}`,
    },
    getBingXTopups: {
        query: (args?: {
            status?: string;
            days?: number;
            network?: string;
            source?: string;
            user_id?: string | number;
            account_id?: string | number;
        }) => {
            const p = new URLSearchParams();
            if (args?.status) p.set("status", args.status);
            if (args?.network) p.set("network", args.network);
            if (args?.source) p.set("source", args.source);
            if (args?.user_id) p.set("user_id", String(args.user_id));
            if (args?.account_id) p.set("account_id", String(args.account_id));
            p.set("days", String(args?.days ?? 30));
            return `/alny-bingx-accounts/topups?${p.toString()}`;
        },
    },
    toggleBingXAddress: {
        query: (args: { id: number; address_id: number; status: number }) => ({
            url: `/alny-bingx-accounts/${args.id}/toggle-address`,
            method: "POST",
            body: {
                address_id: args.address_id,
                status: args.status,
            },
        }),
    },
    addBingXAddress: {
        query: (args: {
            id: number;
            network: string;
            address: string;
        }) => ({
            url: `/alny-bingx-accounts/${args.id}/addresses`,
            method: "POST",
            body: {
                network: args.network,
                address: args.address,
            },
        }),
    },
    resetBingXSumTotal: {
        query: (id: number) => ({
            url: `/alny-bingx-accounts/${id}/reset-sum-total`,
            method: "POST",
        }),
    },
};

export const bingxTags = ["PendingBingx", "BingXWallets", "BingXTopups"] as const;
